import { Select, SelectContent, SelectTrigger, SelectValue, SelectItem, SelectGroup } from '@frontend/shadcn/components/ui/select';
import React, { useState } from 'react';
import { MagnifyingGlassIcon } from '@revfluence/fresh-icons/solid/esm';
import { Input } from '@frontend/shadcn/components/ui/input';
import { Tooltip } from '@revfluence/fresh';
import { cn } from '@frontend/shadcn/lib/utils';

interface NestedSelectItem {
  value: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
  children?: NestedSelectItem[];
}

interface SelectComboboxProps {
  items: NestedSelectItem[];
  value: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  filterPredicate?: (item: NestedSelectItem, searchTerm: string) => boolean;
}

export const SelectCombobox = ({
  items,
  value,
  onValueChange,
  disabled,
  placeholder,
  filterPredicate = (item, searchTerm) => item.value.toLowerCase().includes(searchTerm.toLowerCase()),
}: SelectComboboxProps) => {
  const [searchTerm, setSearchTerm] = useState('');

  const selectedItem = findItemByValue(items, value);

  function findItemByValue(items: NestedSelectItem[], value: string): NestedSelectItem | undefined {
    for (const item of items) {
      if (item.value === value) return item;
      if (item.children?.length) {
        const found = findItemByValue(item.children, value);
        if (found) return found;
      }
    }
    return undefined;
  }

  function renderItems(items: NestedSelectItem[], level = 0) {
    return items
      .filter((item) => filterPredicate(item, searchTerm) || (item.children?.some((child) => filterPredicate(child, searchTerm))))
      .map((item) => {
        const hasChildren = !!item.children?.length;
        if (hasChildren) {
          return (
            <SelectGroup key={item.value}>
              <SelectItem
                value={item.value}
                disabled={item.disabled}
                className={cn(level > 0 && 'pl-8')}
              >
                <div className="flex items-center gap-2">
                  {item.icon && <span className="w-4 h-4 text-primaryMuted">{item.icon}</span>}
                  <span>{item.label}</span>
                </div>
              </SelectItem>
              {renderItems(item.children!, level + 1)}
            </SelectGroup>
          );
        }
        return (
          <SelectItem
            key={item.value}
            value={item.value}
            disabled={item.disabled}
            className={cn(level > 0 && 'pl-8')}
          >
            <div className="flex items-center gap-2">
              {item.icon && <span className="w-4 h-4 text-primaryMuted">{item.icon}</span>}
              <span>{item.label}</span>
            </div>
          </SelectItem>
        );
      });
  }

  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      <Tooltip title={selectedItem?.label || placeholder}>
        <SelectTrigger className="bg-primary-foreground">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
      </Tooltip>
      <SelectContent>
        <div className="px-2 border-b border-border -mx-1">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-1 top-1/2 -translate-y-1/2 text-primaryMuted" />
            <Input
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => {
                e.stopPropagation();
                setSearchTerm(e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              onKeyUp={(e) => e.stopPropagation()}
              onKeyPress={(e) => e.stopPropagation()}
              className="pl-6 border-none focus-visible:ring-0"
              autoFocus
            />
          </div>
        </div>
        <div className="max-h-[300px] overflow-y-auto">
          {renderItems(items)}
        </div>
      </SelectContent>
    </Select>
  );
};
